import './index.scss';
import { AgGridReact } from 'ag-grid-react';
import classNames from 'classnames';
import produce from 'immer';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { debounce } from 'throttle-debounce';
import { useUser } from '~/providers/user';
import Permissions from '~/settings/permissions';
import buildColumnDefs, { ColumnName, defaultColumnDef } from './columnDefinitions';
import { useComparisonGrid } from './provider';
const ComparisonGrid = () => {
    const { gridData, gridRef, setShowActionsControl } = useComparisonGrid();
    const navigate = useNavigate();
    const divRef = useRef(null);
    const onGridResize = debounce(300, () => { fitGridColumn(); });
    useEffect(() => {
        const resizeObserver = new ResizeObserver(onGridResize);
        resizeObserver.observe(divRef.current);
        return () => { resizeObserver.disconnect(); };
    }, [onGridResize]);
    const { hasPermission } = useUser();
    const hideCommentsCol = !hasPermission(Permissions.VIEW_COMPARISON_COMMENTS);
    const [columnDefs] = useState(buildColumnDefs(hideCommentsCol));
    const rowStyle = {
        'expended-row-background-color': '!data.isCollapsed',
    };
    const cellClickedListener = useCallback((prop) => {
        var _a;
        if (prop.colDef.field === ColumnName.Collapsible) {
            prop.api.clearFocusedCell();
            const newGridData = produce(prop.data, (draft) => {
                draft.isCollapsed = !draft.isCollapsed;
            });
            prop.node.setData(newGridData);
            prop.api.redrawRows(prop.data.id);
        }
        else if (((_a = prop.colDef) === null || _a === void 0 ? void 0 : _a.field) === ColumnName.CreatedOn) {
            navigate(`/compare/${prop.data.documentComparisonId}${window.location.search}`);
        }
    }, [gridData]);
    const gridRowId = useCallback((params) => {
        return params.data.projectDocumentComparisonId;
    }, []);
    const fitGridColumn = () => {
        var _a;
        if (!((_a = gridRef.current) === null || _a === void 0 ? void 0 : _a.api))
            return;
        gridRef.current.api.sizeColumnsToFit();
        gridRef.current.api.redrawRows();
        gridRef.current.api.hideOverlay();
    };
    const classnames = classNames('ag-theme-alpine', 'comparison-grid-container', 'rg2-ag-grid-default-style');
    const onSelectionChanged = () => {
        var _a;
        const selectedData = (_a = gridRef.current) === null || _a === void 0 ? void 0 : _a.api.getSelectedRows();
        setShowActionsControl(!!(selectedData === null || selectedData === void 0 ? void 0 : selectedData.length));
    };
    const isRowSelectable = useMemo(() => (params) => { var _a; return !!((_a = params.data) === null || _a === void 0 ? void 0 : _a.isSelectable); }, []);
    return (React.createElement("div", null,
        React.createElement("div", { className: classnames, ref: divRef },
            React.createElement(AgGridReact, { ref: gridRef, getRowId: gridRowId, onFirstDataRendered: fitGridColumn, rowData: gridData, defaultColDef: defaultColumnDef, columnDefs: columnDefs, masterDetail: true, enableCellTextSelection: false, rowClassRules: rowStyle, animateRows: true, rowSelection: 'multiple', suppressRowClickSelection: true, suppressCellFocus: true, onCellClicked: cellClickedListener, onSelectionChanged: onSelectionChanged, isRowSelectable: isRowSelectable }))));
};
export default ComparisonGrid;
